/* eslint-disable i18next/no-literal-string -- const definitions are the source of truth */
import type {Instant, UUID} from '@/types/common'
import type {TriggerSource} from '@/types/identity'

/**
 * Discriminator on {@link LifecycleEvent} — distinguishes identity-side rows
 * from account-side rows in the merged ops timeline (spec §7.3 / §8.4).
 */
export const LifecycleEntityType = {
    IDENTITY: 'IDENTITY',
    ACCOUNT: 'ACCOUNT',
} as const
export type LifecycleEntityType = typeof LifecycleEntityType[keyof typeof LifecycleEntityType]

/**
 * Inline actor projection on a unified lifecycle event response — null for
 * system-driven transitions (spec §3.4 — JML / sweepers / inbound sync have
 * no human actor).
 */
export interface LifecycleEventActor {
    id: UUID
    displayName: string
}

/**
 * Unified row returned by {@code GET /api/i/lifecycle-events} (spec §7.3 /
 * §8.4). Subsumes {@link IdentityLifecycleEvent} and {@code AccountStatusEvent}
 * via the {@link LifecycleEntityType} discriminator.
 *
 * <p>{@code fromState} / {@code toState} are encoded as raw strings because the
 * underlying entity types use two distinct enums (identity lifecycle state vs
 * account status) that we deliberately do not collapse into a single enum.
 * Renderers look up the localised label by entity type + state code.
 */
export interface LifecycleEvent {
    id: UUID
    entityType: LifecycleEntityType
    entityId: UUID
    fromState: string | null
    toState: string
    changedAt: Instant
    actor: LifecycleEventActor | null
    triggerSource: TriggerSource
    triggerRefType: string | null
    triggerRefId: UUID | null
    reason: string | null
}
